import React, { ReactElement, RefCallback, RefObject } from "react";
import { HeaderGroup, TableHeaderProps } from "react-table";
import { DownIcon, UpIcon } from "@src/components/elements";
import {
  StyledTableHeader,
  StyledTableIcons,
} from "@src/components/modules/Table/StyledTable";
import { ColumnWithClassname } from "@src/components/modules/Table/tableInterfaces";

interface Props<T extends Record<string, unknown>> {
  headerGroups: HeaderGroup<T>[];
  getHeaderProps?: (header: HeaderGroup<T>) => Partial<TableHeaderProps>;
  measureRef?: RefObject<HTMLDivElement> | RefCallback<HTMLDivElement>;
  headerBackground?: "gray" | "white";
  width?: number;
  height?: number;
  headerStartIndex?: number;
  headerEndIndex?: number;
  headerRowHeight?: number;
  hasFilters?: boolean;
}

export const TableHead = <T extends Record<string, unknown>>({
  headerGroups,
  getHeaderProps,
  measureRef,
  headerBackground,
  width,
  headerStartIndex = 0,
  headerEndIndex,
  headerRowHeight,
  hasFilters,
}: Props<T>): ReactElement => {
  return (
    <div
      style={{
        width,
        display: "flex",
        flexDirection: "column",
      }}
      className="thead"
      ref={measureRef}
    >
      {headerGroups.map((headerGroup) => {
        const { key, ...restHeaderGroupProps } =
          headerGroup.getHeaderGroupProps();
        let className = restHeaderGroupProps.className ?? "";
        if (headerGroup.headers.every((h) => h.disableSortBy)) {
          className += " header-padding";
        }
        restHeaderGroupProps.className = className;
        return (
          <div
            {...restHeaderGroupProps}
            style={{
              ...restHeaderGroupProps.style,
              flexGrow: 1,
            }}
            className={`${restHeaderGroupProps.className || ""} tr`}
            key={key}
          >
            {headerGroup.headers
              .slice(headerStartIndex, headerEndIndex)
              .map((column) => {
                const sortByToggleProps = column.getSortByToggleProps();

                const {
                  key,
                  onClick: headerOnClick,

                  ...restHeaderProps
                } = (
                  getHeaderProps
                    ? column.getHeaderProps([
                        sortByToggleProps,
                        getHeaderProps(column),
                      ])
                    : column.getHeaderProps([
                        sortByToggleProps,
                        {
                          className: (
                            column as ColumnWithClassname<
                              Record<string, unknown>
                            >
                          ).className,
                          style: (
                            column as ColumnWithClassname<
                              Record<string, unknown>
                            >
                          ).style,
                        },
                      ])
                ) as TableHeaderProps & { onClick?: () => void };
                return (
                  <StyledTableHeader
                    key={key}
                    {...restHeaderProps}
                    className={`${restHeaderProps.className || ""} th`}
                    hasFilter={hasFilters}
                    headerBackground={headerBackground}
                    style={{
                      ...restHeaderProps.style,
                      maxHeight: headerRowHeight,
                      height: headerRowHeight,
                    }}
                  >
                    <>
                      <div className="header-title">
                        <div
                          className="header-title-render"
                          onClick={headerOnClick}
                        >
                          {column.render("Header")}
                        </div>
                        {(sortByToggleProps as HTMLElement).title ===
                          "Toggle SortBy" && (
                          <StyledTableIcons onClick={headerOnClick}>
                            <UpIcon
                              className={
                                column.isSorted && column.isSortedDesc
                                  ? "hide-icon"
                                  : ""
                              }
                            />
                            <DownIcon
                              className={
                                column.isSorted && !column.isSortedDesc
                                  ? "hide-icon"
                                  : ""
                              }
                            />
                          </StyledTableIcons>
                        )}
                      </div>
                      {hasFilters && column.canFilter
                        ? column.render("Filter", {})
                        : null}
                    </>
                  </StyledTableHeader>
                );
              })}
          </div>
        );
      })}
    </div>
  );
};
